import { Link } from "react-router";
import {
  getExportJobDownloadUrl,
  listExportJobs,
  listWorkbooks,
} from "@/lib/api/endpoints";
import { InlineArrow } from "@/components/icons/InlineArrow";
import { ExportStatusBadge } from "./ExportStatusBadge";
import { NewWorkbookActions } from "./NewWorkbookActions";
import { PageLoading, useLoader } from "@/lib/loader";

type RecentWorkbook = {
  id: string;
  originalName: string;
  createdAt: string;
  sheets: unknown;
};

type RecentExportJob = {
  id: string;
  workbookId: string;
  status: string;
  done: boolean;
  createdAt: string;
  workbookName: string | null;
};

export function DashboardPage() {
  const loaded = useLoader(
    () => Promise.all([listWorkbooks(), listExportJobs({ limit: 10 })]),
    [],
  );
  if (loaded.status === "loading" && !loaded.data) return <PageLoading />;

  // The backend or Postgres can be cold-starting; render an empty dashboard.
  const dbWarmingUp = loaded.status === "error";
  const workbooks = loaded.data?.[0].workbooks ?? [];
  const workbookCount = workbooks.length;
  const recentWorkbooks: RecentWorkbook[] = workbooks.slice(0, 5);
  const recentExportJobs: RecentExportJob[] = loaded.data?.[1] ?? [];

  return (
    <div className="mx-auto w-full max-w-6xl space-y-8 px-6 py-8">
      <div>
        <h1 className="text-2xl font-semibold tracking-tight">Dashboard</h1>
        <p className="mt-1 text-sm text-zinc-500 dark:text-zinc-400">
          Upload an Excel workbook, choose a template, and export a Shopify-ready CSV.
        </p>
        {dbWarmingUp && (
          <div className="mt-3 rounded-lg border border-amber-200 dark:border-amber-900/60 bg-amber-50 dark:bg-amber-950/40 px-3 py-2 text-xs text-amber-800 dark:text-amber-300">
            Database is warming up. Refresh in a moment to see your data.
          </div>
        )}
      </div>

      {/* Primary call-to-action + secondary actions */}
      <NewWorkbookActions />

      <div className="grid gap-4 sm:grid-cols-[1fr]">
        <Link
          to="/workbooks"
          className="flex flex-col justify-between rounded-xl border border-zinc-200 dark:border-border bg-white dark:bg-surface p-6 transition hover:border-zinc-300 dark:hover:border-zinc-600 hover:shadow-sm"
        >
          <div>
            <div className="text-sm font-semibold text-zinc-900 dark:text-zinc-100">
              View all workbooks
            </div>
            <p className="mt-1 text-xs text-zinc-500 dark:text-zinc-400">
              {workbookCount} workbook{workbookCount === 1 ? "" : "s"} total
            </p>
          </div>
          <span className="mt-4 text-sm font-medium text-zinc-700 dark:text-zinc-300">
            Browse <InlineArrow />
          </span>
        </Link>
      </div>

      {/* Recent workbooks */}
      <section className="space-y-3">
        <div className="flex items-baseline justify-between">
          <h2 className="text-sm font-semibold uppercase tracking-wide text-zinc-500 dark:text-zinc-400">
            Recent workbooks
          </h2>
          <Link to="/workbooks" className="text-sm font-medium text-zinc-700 dark:text-zinc-300 hover:underline">
            View all <InlineArrow />
          </Link>
        </div>
        {recentWorkbooks.length === 0 ? (
          <div className="rounded-lg border border-dashed border-zinc-300 dark:border-border p-8 text-center text-sm text-zinc-500 dark:text-zinc-400">
            No workbooks yet.{" "}
            <Link to="/upload" className="font-medium text-zinc-700 dark:text-zinc-300 hover:underline">
              Upload one <InlineArrow />
            </Link>
          </div>
        ) : (
          <ul className="divide-y divide-zinc-100 dark:divide-zinc-800 rounded-lg border border-zinc-200 dark:border-border bg-white dark:bg-surface">
            {recentWorkbooks.map((wb) => {
              const sheetCount = Array.isArray(wb.sheets) ? wb.sheets.length : 0;
              return (
                <li key={wb.id}>
                  <Link
                    to={`/workbooks/${wb.id}`}
                    className="flex items-center justify-between px-4 py-3 hover:bg-zinc-50 dark:hover:bg-surface-2"
                  >
                    <div className="min-w-0">
                      <div className="truncate text-sm font-medium text-zinc-900 dark:text-zinc-100">
                        {wb.originalName}
                      </div>
                      <div className="text-xs text-zinc-500 dark:text-zinc-400">
                        {sheetCount} sheet{sheetCount === 1 ? "" : "s"} · {formatRelative(wb.createdAt)}
                      </div>
                    </div>
                    <InlineArrow className="text-zinc-400 dark:text-zinc-500" />
                  </Link>
                </li>
              );
            })}
          </ul>
        )}
      </section>

      {/* Recent exports — global, across all workbooks */}
      <section className="space-y-3">
        <h2 className="text-sm font-semibold uppercase tracking-wide text-zinc-500 dark:text-zinc-400">
          Recent exports
        </h2>
        {recentExportJobs.length === 0 ? (
          <div className="rounded-lg border border-dashed border-zinc-300 dark:border-border p-8 text-center text-sm text-zinc-500 dark:text-zinc-400">
            No exports yet.
          </div>
        ) : (
          <ul className="divide-y divide-zinc-100 dark:divide-zinc-800 rounded-lg border border-zinc-200 dark:border-border bg-white dark:bg-surface">
            {recentExportJobs.map((job) => {
              return (
                <li
                  key={job.id}
                  className="flex items-center justify-between gap-3 px-4 py-3 text-sm"
                >
                  <div className="min-w-0">
                    <Link
                      to={`/workbooks/${job.workbookId}`}
                      className="truncate font-medium text-zinc-900 dark:text-zinc-100 hover:underline"
                    >
                      {job.workbookName ?? "Workbook"}
                    </Link>
                    <div className="text-xs text-zinc-500 dark:text-zinc-400">
                      {formatRelative(job.createdAt)}
                    </div>
                  </div>
                  <div className="flex shrink-0 items-center gap-3">
                    <ExportStatusBadge status={job.status} />
                    {job.done && (
                      <a
                        href={getExportJobDownloadUrl(job.id)}
                        className="text-xs font-medium text-zinc-700 dark:text-zinc-300 hover:underline"
                      >
                        Download
                      </a>
                    )}
                  </div>
                </li>
              );
            })}
          </ul>
        )}
      </section>
    </div>
  );
}

function formatRelative(value: string | Date): string {
  const date = value instanceof Date ? value : new Date(value);
  const diff = Date.now() - date.getTime();
  const m = Math.floor(diff / 60_000);
  if (m < 1) return "just now";
  if (m < 60) return `${m}m ago`;
  const h = Math.floor(m / 60);
  if (h < 24) return `${h}h ago`;
  const d = Math.floor(h / 24);
  if (d < 7) return `${d}d ago`;
  return date.toLocaleDateString();
}
